import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Store from '@apply/store';
import { Header } from '../Header';
import ReviewSubmission from './ReviewSubmission';
import { alertModal, loginAlertModal } from '@apply/ui';
import { useAuthentication } from '../LoginPage';
import { HomePageLink } from '../HomePageLink';

export interface FinalizePageProps {
    state?: Store.ApplyRootStateType;
    actions?: Actions;
}

export interface Actions {
    loadStateFromApi?: (apiId: string, token: string) => Promise<boolean | undefined>;
    onUpdateEsign?: (esign: Store.BorrowerStore.Types.Esign) => void;
    saveStateToApi?: (apiId: string, complete: boolean, token: string) => Promise<Store.BorrowerStore.Types.IdAndToken>;
    onAddProperty: () => void;
    onNeedIpAddress?: () => Promise<string>;
}

export const FinalizePage: React.FC<FinalizePageProps> = ({ state, actions }) => {
    const { apiId } = useParams<{ apiId: string }>();
    const navigate = useNavigate();
    const { logout } = useAuthentication();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>('');
    const [formCompleted] = useState<boolean>(false); // intentionally named different because it does not redirect.

    const { search, pathname } = useLocation();
    const params = new URLSearchParams(search);
    const token = params.get('token');

    useEffect(() => {
        let canceled = false;

        if (apiId) {
            setLoading(true);
            actions
                .loadStateFromApi(apiId, token)
                .then((complete) => {
                    if (complete) {
                        if (state?.properties?.items.every((p) => p.address.state === 'AZ')) {
                            window.location.href = `${window.location.protocol}//${window.location.host}/thankyou`;
                        } else {
                            window.location.href = `/review/${apiId}${token ? '?token=' + token : ''}`;
                        }
                    }
                })
                .catch((err) => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoadError(err);
                    }
                })
                .finally(() => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoading(false);
                    }
                });
        }
        return () => {
            canceled = true;
        };
    }, [apiId, actions, token]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, pathname]);

    const onSubmitClick = () => {
        setLoading(true);
        actions
            .saveStateToApi(apiId, true, token)
            .then(() => {
                setLoading(false);
                if (state?.properties?.items.every((p) => p.address.state === 'AZ')) {
                    navigate(`/thankyou`);
                } else {
                    navigate(`/appraisal/${apiId}${token ? '?token=' + token : ''}`);
                }
            })
            .catch((err) => {
                setLoading(false);
                setLoadError(err);
            });
    };

    if (loading) {
        return (
            <div
                className='overlay'
                data-testid='review-page-loading'
            >
                <div className='overlay__inner'>
                    <div className='overlay__content'>
                        <span className='spinner'></span>
                        <span className='spinner-text'>Loading</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Header />
            <main>
                <HomePageLink />
                <section className='summary'>
                    <h2>Finalize Application</h2>
                </section>
                {/* Review submission has a submit button or a back button. */}
                <ReviewSubmission
                    onUpdateEsign={actions.onUpdateEsign}
                    esign={state?.borrower?.esign}
                    onSubmitClick={onSubmitClick}
                    formCompleted={formCompleted}
                    onNeedIpAddress={actions.onNeedIpAddress}
                />
            </main>
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    state,
});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            loadStateFromApi: (apiId: string, token: string) => dispatch(Store.CrossStore.loadStateFromApi(apiId, null, token)),
            onUpdateEsign: (esign: Store.BorrowerStore.Types.Esign) => dispatch(Store.BorrowerStore.Actions.updateEsign(esign)),
            saveStateToApi: (apiId: string, complete = false, token: string) =>
                dispatch(Store.CrossStore.saveStateToApi(apiId, complete, token)),
            onAddProperty: () => dispatch(Store.PropertiesStore.Actions.addProperty()),
            onNeedIpAddress: () => dispatch(Store.CrossStore.getIpAddress()),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(FinalizePage);
